import type { CSSProperties, HTMLAttributes } from "react";
import { BUTTON_RESET, pressClass } from "../../ui/v2/press";

export interface DayStripDay {
  /** The weekday, already in the caller's locale — `t('today.weekday.mon')`. */
  weekday: string;
  /** The day of the month, as text. */
  dayNumber: string;
  /** The day has been opened before — it gets a dot. @default false */
  open?: boolean;
  /**
   * Outside the loaded week: dimmed, and **not tappable** (02-TODAY.md).
   * @default false
   */
  ghost?: boolean;
}

export interface DayStripProps extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
  days: ReadonlyArray<DayStripDay>;
  /** The strip's accessible name — `t('…')`, never a literal. */
  label: string;
  /** The selected day is always the middle cell. @default 2 */
  selectedIndex?: number;
  onSelect: (index: number) => void;
}

/* `width: 342` is the content column at a 390 viewport (390 − 2 × 24), and the
 * strip is centred in it rather than padded — the template mounts it with
 * `margin: 28px auto 0`. */
const STRIP: CSSProperties = {
  display: "grid",
  width: 342,
  margin: "0 auto",
  position: "relative",
};

/* Radius 12 is DayStrip.jsx's own value. `spacing.css` calls the day cell a
 * pill in a comment, but the component is the authority and 999 on a 68px-wide
 * cell is a different shape entirely. */
const CELL: CSSProperties = {
  ...BUTTON_RESET,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 3,
  padding: "6px 0",
  borderRadius: 12,
};

const SELECTED_BG = "color-mix(in srgb, var(--d-fg) 9%, transparent)";

/* `400 11px/16px mono` + .02em is exactly `--t-label`; the numeral's
 * `400 13px/18px mono` is exactly `--t-data`. */
const WEEKDAY: CSSProperties = {
  font: "var(--t-label)",
  letterSpacing: ".02em",
};

const NUMBER: CSSProperties = { font: "var(--t-data)" };

const DOT: CSSProperties = {
  width: 3,
  height: 3,
  borderRadius: "50%",
};

/**
 * The five days across the top of Today: the selected one in the middle, two
 * on each side. Days outside the loaded week are ghosts — visible, dimmed, and
 * not tappable.
 *
 * Ported from `DayStrip` in
 * `docs/design/handoff/design/components/today/DayStrip.jsx`. The prototype's
 * `<div onClick>` is a `<button>` here, per the v2 atoms.
 */
export function DayStrip({
  days,
  label,
  selectedIndex = 2,
  onSelect,
  className,
  style,
  ...rest
}: DayStripProps): JSX.Element {
  return (
    <div
      aria-label={label}
      className={className}
      style={{
        ...STRIP,
        gridTemplateColumns: `repeat(${days.length || 5},1fr)`,
        ...style,
      }}
      {...rest}
    >
      {days.map((day, index) => {
        const selected = index === selectedIndex;
        const real = !day.ghost;
        const content = (
          <>
            <span
              style={{
                ...WEEKDAY,
                color: selected ? "var(--d-fg-2)" : "var(--d-fg-3)",
              }}
            >
              {day.weekday}
            </span>
            <span
              style={{
                ...NUMBER,
                color: selected ? "var(--d-fg)" : real ? "var(--d-fg-2)" : "var(--d-fg-4)",
              }}
            >
              {day.dayNumber}
            </span>
            <span
              data-day-dot
              aria-hidden="true"
              style={{
                ...DOT,
                background: selected
                  ? "var(--d-fg)"
                  : real && day.open
                    ? "var(--d-fg-3)"
                    : "transparent",
              }}
            />
          </>
        );
        const cellStyle: CSSProperties = {
          ...CELL,
          background: selected ? SELECTED_BG : "transparent",
        };

        return real ? (
          <button
            key={`${day.weekday}-${day.dayNumber}`}
            type="button"
            data-day-cell
            aria-current={selected ? "date" : undefined}
            className={pressClass()}
            onClick={() => onSelect(index)}
            style={cellStyle}
          >
            {content}
          </button>
        ) : (
          <div
            key={`${day.weekday}-${day.dayNumber}`}
            data-day-cell
            /* A ghost can still be the selected day — before the week
             * payload arrives, every cell is one. The background already
             * marks it; `aria-current` says the same thing out loud. */
            aria-current={selected ? "date" : undefined}
            style={cellStyle}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
}

export default DayStrip;
